import React from 'react';
import StepHelpButton from '../StepHelpButton';
import { Meeting } from '../../services/meetingService';
import Workshop2026FaqSection from './Workshop2026FaqSection';
import WorkshopShareInviteBlock from './WorkshopShareInviteBlock';
import styles from '../../styles/workshop2026Page.module.css';

interface Workshop2026AboutTabProps {
  isSignedIn: boolean;
  registrationState?: 'none' | 'interest' | 'confirmed';
  firstSessionLabel?: string;
  meeting?: Meeting | null;
  onGoToWorkshopTab?: () => void;
  onLoginForQuestion: () => void;
  userEmail?: string;
  userDisplayName?: string;
}

const Workshop2026AboutTab: React.FC<Workshop2026AboutTabProps> = ({
  isSignedIn,
  registrationState = 'none',
  firstSessionLabel,
  meeting = null,
  onGoToWorkshopTab,
  onLoginForQuestion,
  userEmail,
  userDisplayName,
}) => {
  return (
    <div className={styles.learnContent}>
      {registrationState === 'interest' && (
        <p className={styles.interestBanner}>
          You&apos;re on the interest list for the 2026 workshop. When you&apos;re ready to commit,
          confirm you&apos;re planning to attend on the Workshop tab — then the Zoom link will
          appear there.{' '}
          {onGoToWorkshopTab && (
            <button type="button" className={styles.textButton} onClick={onGoToWorkshopTab}>
              See Workshop tab →
            </button>
          )}
        </p>
      )}
      {registrationState === 'confirmed' && (
        <p className={styles.interestBanner}>
          You&apos;re confirmed for the 2026 SAA Workshop
          {firstSessionLabel ? ` · First session: ${firstSessionLabel}` : ''}.
          <br />
          The Zoom link and workshop home are on the{' '}
          {onGoToWorkshopTab ? (
            <button type="button" className={styles.textButton} onClick={onGoToWorkshopTab}>
              Workshop tab
            </button>
          ) : (
            'Workshop tab'
          )}{' '}
          -&gt;
        </p>
      )}

      <section className={styles.learnSection}>
        <h2 className={styles.learnHeading}>What is a Step Workshop?</h2>
        <p>
          An SAA Step Workshop is a group of recovering addicts who get together regularly to work through the
          12 steps of SAA and encourage each other. We meet weekly at the same time online via Zoom.
          Workshops typically run 35–60 weeks, depending on group pace and consensus.
        </p>
        <p>
          Recovery often begins in isolation, but healing happens in connection. Working the steps alongside
          others gives us structure, encouragement, momentum, and the shared experience of people who truly
          understand what we&apos;re going through. Hearing others share honestly helps us see ourselves more
          clearly and reminds us that we don&apos;t have to recover alone. What might take years alone can often
          be accomplished in months when we walk the path together.
        </p>
      </section>

      <section className={styles.learnSection}>
        <h2 className={styles.learnHeading}>Is this workshop for me?</h2>
        <ul className={styles.bulletList}>
          <li>Willing to work the 12 steps of SAA</li>
          <li>Can commit to weekly attendance (life happens; we understand)</li>
          <li>Comfortable with online meetings</li>
          <li>No prior step work required to start</li>
        </ul>
        <p>
          This workshop is open to anyone seeking recovery from compulsive sexual behavior — regardless of
          gender, sexual orientation, gender identity, relationship status, or background. The only requirement
          is a desire to stop addictive sexual behavior.
        </p>
        <p>
          You are also welcome and encouraged to join us if you have worked some or all of the steps before — to
          complete the steps, work them again, help others, deepen your recovery, or share your wisdom.
        </p>
        <p className={styles.helperText}>
          You&apos;ll need a sponsor to complete Step One. If you don&apos;t have one yet, you can still join us
          for the first few meetings while you look for one — but you will need a sponsor before completing Step
          One.
        </p>
      </section>

      <section className={styles.learnSection}>
        <h2 className={styles.learnHeading}>What happens each week?</h2>
        <ul className={styles.bulletList}>
          <li>Step-focused reading together, sharing, and encouragement</li>
          <li>
            Work together through Getting Started, the 3 Circles, and steps 1 through 12
          </li>
          <li>
            Between workshop meetings, members track step work in Recovery Commons — inventories, progress, and
            sharing with a sponsor
          </li>
        </ul>
      </section>

      <section className={styles.learnSection}>
        <h2 className={styles.learnHeading}>How registration works</h2>
        <ol className={styles.numberedList}>
          <li>Create a free Recovery Commons account (or sign in if you already have an account)</li>
          <li>Join the interest list on the Workshop tab above - we&apos;ll keep you posted via email</li>
          <li>When you&apos;re ready, tap &ldquo;I&apos;m planning to attend&rdquo;</li>
          <li>After you confirm, the Zoom link appears on the Workshop tab above</li>
        </ol>
        {!isSignedIn && (
          <p className={styles.helperText}>
            When you&apos;re ready, go to the <strong>Workshop</strong> tab to create an account or sign in.
          </p>
        )}
      </section>

      <section className={styles.learnSection}>
        <h2 className={styles.learnHeading}>Share &amp; invite</h2>
        <WorkshopShareInviteBlock />
      </section>

      <section className={styles.learnSection}>
        <h2 className={styles.learnHeading}>Recovery Commons step work</h2>
        <p>
          Members track inventories, prayers, amends lists, and other step work privately in Recovery
          Commons. From the Workshop tab, use <strong>Work my steps</strong> to open your SAA step
          checklist.
        </p>
        <p>
          <StepHelpButton
            stepKey="workshop"
            variant="link"
            linkLabel="How step work in Recovery Commons works"
          />
        </p>
      </section>

      <Workshop2026FaqSection
        isSignedIn={isSignedIn}
        onLoginForQuestion={onLoginForQuestion}
        userEmail={userEmail}
        userDisplayName={userDisplayName}
        meeting={meeting}
      />
    </div>
  );
};

export default Workshop2026AboutTab;
